import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, ArrowRight, CheckCircle2, CreditCard, DollarSign, Calendar, Clock, Loader, AlertTriangle, Info, XCircle, ShieldCheck, Download, Printer, FileText, Check, User, BadgeCheck, Phone, Mail, Wallet, TrendingUp, Percent, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getCooperativeLogoSrc } from "@/components/ui/cooperative-logo";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import LoanConflictModal, { LoanConflictData } from "@/components/LoanConflictModal";
import { GuarantorFields } from "@/components/loans/shared/GuarantorFields";

type LoanTypeCategory = "recommended" | "eligible" | "not_eligible";

interface ClassifiedLoanType {
  loanType: any;
  category: LoanTypeCategory;
  notEligibleReasons: string[];
  score: number;
}

type Step = 1 | 2 | 3 | 4;

export default function ApplyForLoan() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [loanTypeId, setLoanTypeId] = useState("0");
  const [termMonths, setTermMonths] = useState("12");
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [guarantors, setGuarantors] = useState<{ id: number; memberId?: number; name: string; memberCode: string; phone: string; email: string; savings: number }[]>([]);
  const printRef = useRef<HTMLDivElement>(null);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);

  // Conflict modal state
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictCode, setConflictCode] = useState<"ACTIVE_LOAN_EXISTS" | "DUPLICATE_APPLICATION">("ACTIVE_LOAN_EXISTS");
  const [conflictData, setConflictData] = useState<LoanConflictData | null>(null);

  const teacherGroupId = useMemo(() => {
    const raw =
      user?.teacherGroupId ||
      user?.teacher_group_id ||
      user?.groupId ||
      user?.group?.id ||
      user?.teacherGroup?.id;
    const id = Number(raw);
    return !Number.isNaN(id) && id > 0 ? id : null;
  }, [user]);

  const memberId = user?.id || null;

  const { data: loanTypes, isLoading: loanTypesLoading } = useQuery(
    ["loan-types"],
    async () => {
      if (teacherGroupId) {
        try {
          const { data } = await api.get(`/loan-types?groupId=${teacherGroupId}`);
          if (Array.isArray(data.data) && data.data.length > 0) return data.data;
        } catch {}
      }
      const { data } = await api.get("/loan-types");
      const items = Array.isArray(data.data) ? data.data : [];
      return items.filter((lt: any) => lt.isActive !== false);
    },
    { enabled: !!user }
  );

  const { data: groupSettings } = useQuery(
    ["teacherGroup"],
    async () => {
      const { data } = await api.get("/teacher-groups");
      return data.data?.[0];
    }
  );

  const { data: orgSettings } = useQuery(
    ["org-settings"],
    async () => {
      const { data } = await api.get("/settings");
      return data.data || {};
    },
    { staleTime: 300000 }
  );

  const { data: memberProfile, isLoading: memberProfileLoading } = useQuery(
    ["member-profile", user?.id],
    async () => {
      const endpoint = user?.type === "member" ? `/members/${user?.id}` : `/teachers/${user?.id}`;
      const { data } = await api.get(endpoint);
      return data.data || data;
    },
    { enabled: !!user?.id, staleTime: 60000 }
  );

  const memberInfo = useMemo(() => {
    const mp = memberProfile || {};
    const fullName =
      mp.fullName ||
      mp.fullNames ||
      `${mp.lastName || ""} ${mp.firstName || ""}`.trim() ||
      user?.fullName ||
      user?.name ||
      "Not provided";
    const phone = mp.phone || mp.phoneNumber || user?.phone || "Not provided";
    const email = mp.email || user?.email || "Not provided";
    const memberNumber = mp.memberCode || mp.teacherId || mp.memberNumber || user?.teacherId || user?.memberCode || "N/A";
    return { fullName, phone, email, memberNumber };
  }, [memberProfile, user]);

  const { data: userLoans } = useQuery(
    ["my-loans-check", user?.id],
    async () => {
      const { data: res } = await api.get(`/teachers/${user?.id}/loans`);
      const d = res.data || res;
      return { loans: Array.isArray(d.loans) ? d.loans : [] };
    },
    { enabled: !!user?.id }
  );

  const hasActiveLoan = useMemo(() => {
    const loans = userLoans?.loans || [];
    const activeLoans = loans.filter((l: any) => ["active", "disbursed"].includes(l.status?.toLowerCase()));
    const loanPolicies = (orgSettings as any)?.loanPolicies || {};
    const allowMultiple = loanPolicies.allowMultipleActiveLoans === true;
    const maxActive = Number(loanPolicies.maxActiveLoansPerMember) || 1;
    if (allowMultiple) {
      return activeLoans.length >= maxActive;
    }
    return activeLoans.length > 0;
  }, [userLoans, orgSettings]);

  const outstandingLoanBalance = useMemo(() => {
    const loans = userLoans?.loans || [];
    return loans
      .filter((l: any) => ["active", "disbursed"].includes(l.status?.toLowerCase()))
      .reduce((sum: number, l: any) => sum + (Number(l.dueAmount) || Number(l.amount) || 0), 0);
  }, [userLoans]);

  const pendingApplicationCount = useMemo(() => {
    const loans = userLoans?.loans || [];
    return loans.filter((l: any) => ["pending", "approved", "pending_approval", "approved_by_leaders", "ready_for_disbursement"].includes(l.status?.toLowerCase())).length;
  }, [userLoans]);

  // Loan policies from system settings
  const loanPolicies = (orgSettings as any)?.loanPolicies || {};
  const allowWithOutstanding = loanPolicies.allowLoanWithOutstandingBalance === true;
  const deductOutstanding = loanPolicies.deductOutstandingBalanceFromEligibility !== false;
  const allowMultiplePending = loanPolicies.allowMultiplePendingLoanApplications !== false;
  const maxPendingApplications = Number(loanPolicies.maxPendingApplicationsPerMember) || 1;
  const globalMinLoan = Number(loanPolicies.globalMinimumLoanAmount) || 0;
  const globalMaxLoan = Number(loanPolicies.globalMaximumLoanAmount) || Infinity;

  // Blocking logic: outstanding loan blocks, active loan blocks — pending is a warning, not a block
  const isBlockedByOutstanding = !allowWithOutstanding && outstandingLoanBalance > 0;
  const isBlockedByPending = !allowMultiplePending && pendingApplicationCount >= maxPendingApplications;
  const isBlocked = isBlockedByOutstanding || hasActiveLoan;

  const defaultLoanType = useMemo(() => ({
    id: 0,
    name: "Monthly",
    displayName: "Standard Member Loan",
    interestRate: Number(groupSettings?.loanInterestRate || 12),
    defaultTermMonths: 12,
    maxTermMonths: 12,
    isActive: true,
  }), [groupSettings]);

  const displayLoanTypes = (loanTypes && loanTypes.length > 0 ? loanTypes : [defaultLoanType]);

  const { data: memberContributions, isLoading: contributionsLoading } = useQuery(
    ["member-contributions-self"],
    async () => {
      const { data } = await api.get("/contributions/my?limit=200").catch(() => ({ data: { data: [] } }));
      return data.data || data.results || [];
    },
    { enabled: !!user }
  );

  const { data: memberPayments = [] } = useQuery(
    ["member-payments-self"],
    async () => {
      const { data } = await api.get("/member-payments/my?limit=200").catch(() => ({ data: [] }));
      return Array.isArray(data) ? data : data?.data || data?.results || [];
    },
    { enabled: !!user }
  );

  const resolvedTeacherId = useMemo(() => {
    const list = memberContributions || [];
    if (list.length > 0) {
      const t = list[0]?.teacher;
      if (t?.id) return Number(t.id);
    }
    return user?.id || null;
  }, [memberContributions, user?.id]);

  const resolvedGroupId = useMemo(() => {
    const list = memberContributions || [];
    if (list.length > 0) {
      const g = list[0]?.teacherGroup;
      if (g?.id) return Number(g.id);
    }
    return teacherGroupId;
  }, [memberContributions, teacherGroupId]);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "loan-review-print-styles";
    style.textContent = `
      @media print {
        @page { size: A4 portrait; margin: 12mm; }
        body * { visibility: hidden !important; }
        .loan-review-print-area, .loan-review-print-area * { visibility: visible !important; }
        .loan-review-print-area { position: absolute; left: 0; top: 0; width: 100%; background: white !important; }
        .loan-review-print-area .print\\:hidden { display: none !important; }
        .loan-review-print-area .print\\:block { display: block !important; }
        .loan-review-print-area .shadow-sm { box-shadow: none !important; }
        .loan-review-print-area .border { border-color: #ccc !important; }
        .loan-review-print-area .bg-\\[\\#009A44\\] { background-color: #007a36 !important; color: white !important; }
        .loan-review-print-area .text-\\[\\#009A44\\] { color: #007a36 !important; }
        .loan-review-print-area .bg-green-50 { background-color: #f0f7f0 !important; }
        .loan-review-print-area .bg-amber-50 { background-color: #fff8f0 !important; }
        .loan-review-print-area .print-break-inside { break-inside: avoid; page-break-inside: avoid; }
        .loan-review-print-area table { border-collapse: collapse; width: 100%; }
        .loan-review-print-area th, .loan-review-print-area td { border: 1px solid #ddd; padding: 6px 8px; font-size: 9pt; }
        .loan-review-print-area img { max-width: 100%; }
        .loan-review-print-area .print-hide-if-empty:empty { display: none; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById("loan-review-print-styles");
      if (el) el.remove();
    };
  }, []);

  const contributionsTotal = (memberContributions || [])
    .filter((c: any) => c.isVerified || c.verified || c.verificationStatus === "Verified" || c.status === "verified")
    .reduce((sum: number, c: any) => sum + (Number(c.amount) || Number(c.depositAmount) || 0), 0);

  const approvedSavingsTotal = (memberPayments || [])
    .filter((p: any) => String(p.status || "").toLowerCase() === "approved")
    .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);

  const memberSavingAmount = Number(memberProfile?.savingAmount || 0);

  const teacherSavings = contributionsTotal + approvedSavingsTotal + memberSavingAmount;

  const maxLoanMultiplier = Number(loanPolicies.maxLoanMultiplier) || Number(groupSettings?.maxLoanMultiplier) || 3;
  const eligibleSavings = deductOutstanding ? Math.max(0, teacherSavings - outstandingLoanBalance) : teacherSavings;
  const maxLoanAmount = eligibleSavings * maxLoanMultiplier;

  const selectedLoanType = useMemo(
    () => (loanTypes || []).find((lt: any) => String(lt.id) === loanTypeId),
    [loanTypes, loanTypeId]
  );

  // Classify all loan types into recommended / eligible / not_eligible
  const classifiedLoanTypes = useMemo((): ClassifiedLoanType[] => {
    if (!displayLoanTypes) return [];
    const fmt = (v: number) => v.toLocaleString() + " FRW";
    return displayLoanTypes.map((lt: any) => {
      const ltMin = Number(lt.minAmount) || 0;
      const ltInterest = Number(lt.interestRate) || 999;
      const memberMaxLoan = maxLoanAmount;
      const reasons: string[] = [];

      if (isBlocked) {
        if (isBlockedByOutstanding) reasons.push("Active loan balance must be cleared first");
        else reasons.push("Active loan limit reached");
      }
      if (memberMaxLoan < ltMin) {
        reasons.push(`Savings too low — min ${fmt(ltMin)}, your max: ${fmt(memberMaxLoan)}`);
      }

      const isEligible = reasons.length === 0;
      let score = 0;
      if (isEligible) {
        score += Math.max(0, 100 - ltInterest * 4);
        score += Math.min(50, (ltMin / 500000) * 50);
        const dt = Number(lt.defaultTermMonths) || 12;
        score += Math.min(20, (dt / 24) * 20);
      }

      return {
        loanType: lt,
        category: isEligible ? "eligible" : "not_eligible",
        notEligibleReasons: reasons,
        score,
      };
    });
  }, [displayLoanTypes, isBlocked, isBlockedByOutstanding, maxLoanAmount]);

  // Find best eligible, then mark as "recommended"
  const recommendedClassification = useMemo(() => {
    const eligible = classifiedLoanTypes.filter((c) => c.category === "eligible");
    if (eligible.length === 0) return null;
    return eligible.reduce((best, curr) => {
      if (curr.score !== best.score) return curr.score > best.score ? curr : best;
      const aInt = Number(curr.loanType.interestRate) || 999;
      const bInt = Number(best.loanType.interestRate) || 999;
      if (aInt !== bInt) return aInt < bInt ? curr : best;
      const aTerm = Number(curr.loanType.defaultTermMonths) || 0;
      const bTerm = Number(best.loanType.defaultTermMonths) || 0;
      if (aTerm !== bTerm) return aTerm > bTerm ? curr : best;
      const aMax = Number(curr.loanType.maxAmount) || 0;
      const bMax = Number(best.loanType.maxAmount) || 0;
      return aMax > bMax ? curr : best;
    });
  }, [classifiedLoanTypes]);

  // Derive the final classified list with "recommended" set on the best one
  const finalClassifiedLoanTypes = useMemo(() => {
    if (!recommendedClassification) return classifiedLoanTypes;
    return classifiedLoanTypes.map((c) =>
      c.loanType.id === recommendedClassification.loanType.id
        ? { ...c, category: "recommended" as const }
        : c
    );
  }, [classifiedLoanTypes, recommendedClassification]);

  const eligibleLoanTypes = useMemo(
    () => finalClassifiedLoanTypes.filter((c) => c.category !== "not_eligible").map((c) => c.loanType),
    [finalClassifiedLoanTypes]
  );

  // Auto-select the recommended loan type when data loads
  useEffect(() => {
    if (loanTypeId !== "0") return;
    if (loanTypesLoading || contributionsLoading) return;
    if (isBlocked || !recommendedClassification) return;
    const best = recommendedClassification.loanType;
    setLoanTypeId(String(best.id));
    if (best.defaultTermMonths) setTermMonths(String(best.defaultTermMonths));
  }, [recommendedClassification, loanTypesLoading, contributionsLoading, isBlocked, loanTypeId]);

  // Auto-fill repayment term when loan type changes
  useEffect(() => {
    if (loanTypeId !== "0" && selectedLoanType?.defaultTermMonths) {
      const currentTerm = parseInt(termMonths) || 0;
      if (currentTerm === 0 || currentTerm === 12) {
        setTermMonths(String(selectedLoanType.defaultTermMonths));
      }
    }
  }, [loanTypeId, selectedLoanType]);

  const interestRate = Number(selectedLoanType?.interestRate || groupSettings?.loanInterestRate || 12);
  const numericAmount = parseFloat(amount) || 0;
  const numericTerm = parseInt(termMonths) || 12;
  const upfrontInterest = Math.round(numericAmount * (interestRate / 100));
  const netDisbursedAmount = numericAmount - upfrontInterest;
  const totalRepayment = numericAmount;
  const monthlyInstallment = numericTerm > 0 ? totalRepayment / numericTerm : 0;

  const loanTypeMinAmount = Number(selectedLoanType?.minAmount) || 1000;
  const loanTypeMaxAmount = selectedLoanType?.maxAmount
    ? Math.min(Number(selectedLoanType.maxAmount), maxLoanAmount)
    : maxLoanAmount;

  const effectiveMin = globalMinLoan > 0 ? Math.max(loanTypeMinAmount, globalMinLoan) : loanTypeMinAmount;
  const effectiveMax = globalMaxLoan < Infinity
    ? Math.min(loanTypeMaxAmount, globalMaxLoan)
    : loanTypeMaxAmount;

  const amountValid = numericAmount === 0 || (numericAmount >= effectiveMin && numericAmount <= effectiveMax);
  const hasSavings = eligibleSavings > 0;
  const loanTypeSelected = loanTypeId !== "0";

  const requiredGuarantorCount = selectedLoanType?.requiredInsurerCount || (selectedLoanType?.requiresInsurers ? 1 : 0);
  const guarantorsFilled = requiredGuarantorCount === 0 || (guarantors.length >= requiredGuarantorCount && guarantors.slice(0, requiredGuarantorCount).every((g) => g.name && g.memberCode));
  const canNextStep1 = amountValid && numericAmount > 0 && hasSavings && loanTypeSelected && !isBlocked && guarantorsFilled;
  const canNextStep2 = numericTerm >= 1 && numericTerm <= 60;

  const handleSubmit = async () => {
    if (isBlocked) {
      if (isBlockedByOutstanding) {
        toast.error("You have an active loan with an outstanding balance. Please clear it before applying for a new loan.");
      } else {
        toast.error("You have reached the active loan limit. Only one active loan is allowed.");
      }
      return;
    }
    // Warn about pending loans but allow proceeding
    if (isBlockedByPending) {
      const confirmed = window.confirm(
        `You already have ${pendingApplicationCount} pending loan application(s) awaiting approval. Are you sure you want to apply for another loan?`
      );
      if (!confirmed) return;
    }
    setSubmitting(true);
    try {
      const selectedType = displayLoanTypes.find((lt: any) => String(lt.id) === loanTypeId);
      const loanTypeValue = selectedType?.name || "Monthly";

      const cleanPayload: Record<string, any> = {
        amount: parseInt(String(Math.round(Number(numericAmount))), 10),
        termMonths: parseInt(String(Number(numericTerm)), 10),
        purpose: purpose || "General",
        loanType: loanTypeValue,
        teacherId: resolvedTeacherId || user?.id || 0,
        teacherGroupId: resolvedGroupId || teacherGroupId || 0,
        totalSavings: parseInt(String(Math.round(Number(teacherSavings))), 10),
        eligibleSavings: parseInt(String(Math.round(Number(eligibleSavings))), 10),
        maxLoanAmount: parseInt(String(Math.round(Number(maxLoanAmount))), 10),
      };
      if (loanTypeId && parseInt(loanTypeId, 10) > 0) {
        cleanPayload.loanTypeId = parseInt(loanTypeId, 10);
      }

      if (guarantors.length > 0) {
        cleanPayload.guarantors = guarantors.map((g) => ({
          name: g.name,
          memberId: g.memberId || undefined,
          memberCode: g.memberCode || undefined,
          phone: g.phone || undefined,
          email: g.email || undefined,
        }));
      }

      console.log("[ApplyForLoan] Submitting payload:", JSON.stringify(cleanPayload, null, 2));
      const response = await api.post("/loans/apply", cleanPayload);
      console.log("[ApplyForLoan] Submit success:", response.data);
      setStep(4);
      queryClient.invalidateQueries(["my-loans"]);
    } catch (err: any) {
      const responseData = err?.response?.data;
      const code = responseData?.code;
      const status = err?.response?.status;
      console.error("[ApplyForLoan] Submit error:", {
        status,
        code,
        message: responseData?.message,
        error: responseData?.error,
        fullResponse: responseData,
      });

      if (code === "ACTIVE_LOAN_EXISTS" || code === "DUPLICATE_APPLICATION") {
        setConflictCode(code);
        setConflictData(responseData.data);
        setConflictOpen(true);
      } else {
        const msg = responseData?.message || responseData?.error || "Failed to submit application. Please try again.";
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (val: number) => val.toLocaleString() + " FRW";

  const orgInfo = useMemo(() => {
    const org = orgSettings?.organization || {};
    return {
      name: org.cooperativeName || groupSettings?.cooperativeName || "CAISSE D'ENTRAIDE IPRC KARONGI",
      registrationNumber: org.registrationNumber || "N/A",
      address: org.physicalAddress || "Karongi, Rwanda",
      phone: org.contactPhone || "+250 793 502 827",
      email: org.contactEmail || "caisse.iprcwest@gmail.com",
    };
  }, [orgSettings, groupSettings]);

  const applicationNumber = useMemo(() => {
    const d = new Date();
    return `APP-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(Math.floor(Math.random() * 10000)).padStart(4, "0")}`;
  }, []);

  const repaymentSchedule = useMemo(() => {
    const schedule = [];
    const principalPortion = numericTerm > 0 ? numericAmount / numericTerm : 0;
    let balance = numericAmount;
    const startDate = new Date();

    for (let i = 1; i <= numericTerm; i++) {
      const dueDate = new Date(startDate);
      dueDate.setMonth(dueDate.getMonth() + i);
      const amountDue = i === numericTerm ? balance : Math.floor(principalPortion);
      balance -= amountDue;
      schedule.push({
        installment: i,
        dueDate: format(dueDate, "MMM d, yyyy"),
        principal: Math.round(amountDue),
        interest: 0,
        balance: Math.round(Math.max(balance, 0)),
        amountDue: Math.round(amountDue),
      });
    }
    return schedule;
  }, [numericAmount, numericTerm]);

  const eligibilityItems = useMemo(() => ({
    amountWithinLimit: numericAmount > 0 && numericAmount <= maxLoanAmount && numericAmount >= effectiveMin,
    savingsRequirement: teacherSavings > 0,
    membershipDuration: true,
    sharesSatisfied: true,
    loanPolicySatisfied: !isBlocked,
  }), [numericAmount, maxLoanAmount, effectiveMin, teacherSavings, isBlocked]);

  const processingFee = Math.round(numericAmount * 0.01);
  const insuranceFee = Math.round(numericAmount * 0.005);
  const otherCharges = 0;
  const totalCost = numericAmount + processingFee + insuranceFee + otherCharges;

  const generatePDF = async () => {
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pw = 210;
      const m = 15;
      const cw = pw - 2 * m;
      const totalPdfPages = (pdf as any).internal?.getNumberOfPages?.() ?? 1;

      const drawHeader = (isFirstPage: boolean) => {
        try {
          pdf.addImage(getCooperativeLogoSrc(), "JPEG", m, 10, 22, 22);
        } catch {
          pdf.setFontSize(10);
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(0, 100, 0);
          pdf.text("IKIMINA", m + 4, 20);
        }
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 0, 0);
        pdf.text(orgInfo.name, pw / 2, 16, { align: "center" });
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(80, 80, 80);
        pdf.text(`Reg: ${orgInfo.registrationNumber} | ${orgInfo.address} | ${orgInfo.phone} | ${orgInfo.email}`, pw / 2, 22, { align: "center" });
        pdf.setDrawColor(0, 100, 0);
        pdf.setLineWidth(0.5);
        pdf.line(m, 25, pw - m, 25);
        if (isFirstPage) {
          pdf.setFontSize(16);
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(0, 80, 0);
          pdf.text("LOAN APPLICATION SUMMARY", pw / 2, 33, { align: "center" });
          pdf.setDrawColor(0, 100, 0);
          pdf.setLineWidth(0.3);
          pdf.line(m, 36, pw - m, 36);
        }
      };

      const drawFooter = (pageNum: number, totalPages: number) => {
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
        pdf.line(m, 285, pw - m, 285);
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(120, 120, 120);
        pdf.text(`Generated: ${format(new Date(), "yyyy-MM-dd HH:mm")} | Generated by Cooperative Management System`, m, 290);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pw - m, 290, { align: "right" });
      };

      drawHeader(true);

      // Helper: check if we need a new page and add footer
      const checkPageBreak = (neededSpace: number) => {
        if (y + neededSpace > 270) {
          drawFooter((pdf as any).internal?.getNumberOfPages?.() ?? 1, (pdf as any).internal?.getNumberOfPages?.() ?? 1);
          pdf.addPage();
          drawHeader(false);
          y = 42;
        }
      };

      // Application reference section
      let y = 42;
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);
      const appInfo = [
        `Application Number: ${applicationNumber}`,
        `Application Date: ${format(new Date(), "yyyy-MM-dd")}`,
        `Member ID: ${memberInfo.memberNumber}`,
        `Status: Draft`,
      ];
      const mid = pw / 2;
      appInfo.forEach((line, i) => {
        const col = i < 2 ? m : mid;
        const row = i % 2;
        pdf.text(line, col, y + row * 5);
      });

      // Applicant Information
      y += 14;
      checkPageBreak(30);
      pdf.setFillColor(0, 80, 0);
      pdf.rect(m, y, cw, 6, "F");
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(255, 255, 255);
      pdf.text("APPLICANT INFORMATION", m + 2, y + 4.5);

      y += 10;
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);
      const memberFields = [
        `Full Name: ${memberInfo.fullName}`,
        `Member Number: ${memberInfo.memberNumber}`,
      ];
      const memberFields2 = [
        `Phone: ${memberInfo.phone}`,
        `Email: ${memberInfo.email}`,
      ];
      memberFields.forEach((f, i) => { pdf.text(f, m + 2, y + i * 5); });
      memberFields2.forEach((f, i) => { pdf.text(f, mid + 2, y + i * 5); });

      y += 14;
      pdf.setFont("helvetica", "normal");
      const savingsLine = `Savings Balance: ${formatCurrency(teacherSavings)}`;
      const eligibleLine = `Maximum Eligible Loan: ${formatCurrency(maxLoanAmount)}`;
      pdf.text(savingsLine, m + 2, y);
      pdf.text(eligibleLine, mid + 2, y);

      // Loan Details
      y += 10;
      checkPageBreak(35);
      pdf.setFillColor(0, 80, 0);
      pdf.rect(m, y, cw, 6, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(255, 255, 255);
      pdf.text("LOAN DETAILS", m + 2, y + 4.5);

      y += 10;
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);
      const loanFields = [
        { label: "Loan Type", value: selectedLoanType?.name || "N/A" },
        { label: "Loan Category", value: selectedLoanType?.category || selectedLoanType?.loanCategory || "Standard" },
        { label: "Requested Amount", value: formatCurrency(numericAmount) },
        { label: "Purpose", value: purpose || "General" },
      ];
      const loanFields2 = [
        { label: "Interest Rate", value: `${interestRate}%` },
        { label: "Interest Calculation", value: selectedLoanType?.interestType || "Simple Interest" },
        { label: "Loan Duration", value: formatTerm(numericTerm) },
        { label: "Processing Fee", value: formatCurrency(processingFee) },
      ];
      loanFields.forEach((f, i) => {
        pdf.text(`${f.label}: ${f.value}`, m + 2, y + i * 5);
      });
      loanFields2.forEach((f, i) => {
        pdf.text(`${f.label}: ${f.value}`, mid + 2, y + i * 5);
      });

      y += 24;
      pdf.setFont("helvetica", "normal");
      pdf.text(`Insurance Fee: ${formatCurrency(insuranceFee)}`, m + 2, y);
      pdf.text(`Required Guarantors: ${selectedLoanType?.requiredInsurerCount || 0}`, mid + 2, y);

      // Financial Summary
      y += 10;
      checkPageBreak(30);
      pdf.setFillColor(0, 80, 0);
      pdf.rect(m, y, cw, 6, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(255, 255, 255);
      pdf.text("FINANCIAL SUMMARY", m + 2, y + 4.5);

      y += 10;
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);
      const summaryItems = [
        { label: "Requested Amount", value: formatCurrency(numericAmount), bold: false },
        { label: "Interest Deducted Upfront", value: formatCurrency(upfrontInterest), bold: false },
        { label: "Net Amount You Receive", value: formatCurrency(netDisbursedAmount), bold: false },
        { label: "Total Amount To Repay", value: formatCurrency(totalRepayment), bold: false },
      ];
      let sx = m + 2;
      summaryItems.forEach((item, idx) => {
        const x = m + idx * (cw / 4);
        pdf.text(item.label, x + 2, y);
        pdf.setFont("helvetica", "bold");
        pdf.text(item.value, x + 2, y + 5);
        pdf.setFont("helvetica", "normal");
      });

      y += 12;
      pdf.setDrawColor(0, 100, 0);
      pdf.setLineWidth(0.3);
      pdf.line(m, y, pw - m, y);

      // Highlighted items
      y += 4;
      const highlights = [
        { label: "Requested Amount", value: formatCurrency(numericAmount) },
        { label: "Monthly Installment", value: formatCurrency(Math.round(monthlyInstallment)) },
        { label: "Total Repayment", value: formatCurrency(Math.round(totalRepayment)) },
      ];
      highlights.forEach((h, idx) => {
        const x = m + idx * (cw / 3);
        pdf.setFillColor(240, 248, 235);
        pdf.roundedRect(x + 1, y, cw / 3 - 2, 12, 1, 1, "F");
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(50, 50, 50);
        pdf.text(h.label, x + 3, y + 4);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor(0, 80, 0);
        pdf.text(h.value, x + 3, y + 10);
      });

      // Repayment Schedule
      y += 18;
      checkPageBreak(40);
      pdf.setFillColor(0, 80, 0);
      pdf.rect(m, y, cw, 6, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(255, 255, 255);
      pdf.text("REPAYMENT SCHEDULE", m + 2, y + 4.5);

      y += 8;
      if (repaymentSchedule.length > 0) {
        const tableData = repaymentSchedule.slice(0, Math.min(6, repaymentSchedule.length)).map((row) => [
          row.installment.toString(),
          row.dueDate,
          `${Math.round(row.principal).toLocaleString()}`,
          `${Math.round(row.interest).toLocaleString()}`,
          `${Math.round(row.balance).toLocaleString()}`,
          `${Math.round(row.amountDue).toLocaleString()}`,
        ]);

        autoTable(pdf, {
          startY: y,
          head: [[{ content: "Inst.", styles: { halign: "center" } }, { content: "Due Date", styles: { halign: "center" } }, { content: "Principal (RWF)", styles: { halign: "right" } }, { content: "Interest (RWF)", styles: { halign: "right" } }, { content: "Balance (RWF)", styles: { halign: "right" } }, { content: "Amount Due (RWF)", styles: { halign: "right" } }]],
          body: tableData,
          theme: "grid",
          headStyles: { fillColor: [0, 80, 0], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7, halign: "center" },
          bodyStyles: { fontSize: 7, cellPadding: 1.5 },
          alternateRowStyles: { fillColor: [245, 248, 242] },
          columnStyles: {
            0: { cellWidth: 14, halign: "center" },
            1: { cellWidth: 30, halign: "center" },
            2: { cellWidth: 34, halign: "right" },
            3: { cellWidth: 34, halign: "right" },
            4: { cellWidth: 34, halign: "right" },
            5: { cellWidth: 34, halign: "right" },
          },
          margin: { top: 10, right: m, bottom: 25, left: m },
          didDrawPage: (data: any) => {
            const tp = (pdf as any).internal?.getNumberOfPages?.() ?? 1;
            drawFooter(data.pageNumber, tp);
          },
        });

        y = (pdf as any).lastAutoTable.finalY + 4;
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "italic");
        pdf.setTextColor(100, 100, 100);
        if (repaymentSchedule.length > 6) {
          pdf.text(`...remaining ${repaymentSchedule.length - 6} repayments continue until loan maturity.`, m, y);
          y += 4;
        } else {
          pdf.text("Full repayment schedule shown above.", m, y);
          y += 4;
        }
      }

      // Eligibility
      y += 2;
      checkPageBreak(30);
      pdf.setFillColor(0, 80, 0);
      pdf.rect(m, y, cw, 6, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(255, 255, 255);
      pdf.text("ELIGIBILITY VERIFICATION", m + 2, y + 4.5);

      y += 10;
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);
      const eItems = [
        { label: "Loan amount within limit", ok: eligibilityItems.amountWithinLimit },
        { label: "Savings requirement satisfied", ok: eligibilityItems.savingsRequirement },
        { label: "Membership duration satisfied", ok: eligibilityItems.membershipDuration },
        { label: "Required shares satisfied", ok: eligibilityItems.sharesSatisfied },
        { label: "Loan policy satisfied", ok: eligibilityItems.loanPolicySatisfied },
      ];
      eItems.forEach((item, i) => {
        const icon = item.ok ? "Yes" : "No";
        const lx = m + 2 + (i < 3 ? 0 : cw / 2);
        const ly = y + (i % 3) * 5;
        if (item.ok) { pdf.setTextColor(0, 100, 0); } else { pdf.setTextColor(200, 80, 0); }
        pdf.text(`${icon}: ${item.label}`, lx, ly);
        pdf.setTextColor(50, 50, 50);
      });

      // Loan Terms
      y += 20;
      checkPageBreak(35);
      pdf.setFillColor(0, 80, 0);
      pdf.rect(m, y, cw, 6, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(255, 255, 255);
      pdf.text("IMPORTANT LOAN TERMS", m + 2, y + 4.5);

      y += 10;
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);
      const terms = [
        "1. Late repayment penalties apply as per cooperative policy.",
        "2. Processing fee is non-refundable once the application is processed.",
        "3. Insurance policy covers the outstanding loan balance in case of unforeseen events.",
        "4. Loan approval depends on committee review and credit assessment.",
        "5. Loan disbursement is subject to verification of provided information.",
        "6. Providing false information may lead to rejection and disciplinary action.",
      ];
      terms.forEach((t, i) => {
        pdf.text(t, m + 2, y + i * 4.5);
      });

      // Declaration
      y += 30;
      checkPageBreak(30);
      pdf.setDrawColor(0, 100, 0);
      pdf.setFillColor(245, 250, 240);
      pdf.roundedRect(m, y, cw, 20, 2, 2, "FD");
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(0, 60, 0);
      pdf.text("MEMBER DECLARATION", m + 3, y + 4);
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);
      const declText = "I certify that the information provided in this application is accurate and complete. I understand that providing false information may result in rejection of my application and other actions according to cooperative policies.";
      const splitDecl = pdf.splitTextToSize(declText, cw - 6);
      pdf.text(splitDecl, m + 3, y + 8);

      // Signature section
      y += 24;
      pdf.setDrawColor(0, 100, 0);
      pdf.setLineWidth(0.3);
      pdf.line(m, y, pw / 2 - 5, y);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);
      pdf.text("Applicant Signature", m, y + 4);
      pdf.text("Date: ________________", pw / 2 + 5, y + 4);

      y += 12;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(m, y, pw - m, y);
      y += 4;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(0, 60, 0);
      pdf.text("Approval Section - Reserved for Cooperative Use", pw / 2, y, { align: "center" });
      y += 6;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(50, 50, 50);
      const roles = ["Loan Officer", "Committee", "Treasurer", "Manager"];
      const rw = cw / 4;
      roles.forEach((role, i) => {
        const x = m + i * rw;
        pdf.setDrawColor(180, 180, 180);
        pdf.line(x, y + 8, x + rw - 4, y + 8);
        pdf.text(role, x, y + 12);
        pdf.text("Signature: ___________", x, y + 16);
        pdf.text("Date: ______________", x, y + 20);
      });

      // Apply footer to all pages
      const tp = (pdf as any).internal?.getNumberOfPages?.() ?? 1;
      for (let i = 1; i <= tp; i++) {
        pdf.setPage(i);
        drawFooter(i, tp);
      }

      const safeName = memberInfo.fullName.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "");
      pdf.save(`Loan_Application_${memberInfo.memberNumber}_${format(new Date(), "yyyy-MM-dd")}.pdf`);
      toast.success("Loan application PDF downloaded successfully");
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const openPrintPreview = () => {
    setPrintPreviewOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closePrintPreview = () => {
    setPrintPreviewOpen(false);
    document.body.style.overflow = "";
  };

  const handlePrint = () => {
    window.print();
    closePrintPreview();
  };

  const formatTerm = (months: number) => {
    if (!months || months <= 0) return "";
    if (months >= 12) {
      const years = Math.floor(months / 12);
      const rem = months % 12;
      return rem > 0 ? `${years} yr ${rem} mo` : `${years} ${years === 1 ? "Year" : "Years"}`;
    }
    return `${months} ${months === 1 ? "Month" : "Months"}`;
  };

  return (
    <div>
      {/* Conflict Modal */}
      {conflictData && (
        <LoanConflictModal
          isOpen={conflictOpen}
          onClose={() => setConflictOpen(false)}
          conflictCode={conflictCode}
          conflictData={conflictData}
          isAdmin={false}
          isPresident={false}
        />
      )}

      {/* Print Preview Overlay */}
      {printPreviewOpen && (
        <div className="fixed inset-0 z-50 bg-slate-100 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white shrink-0 no-print">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Print Preview</h2>
              <p className="text-sm text-slate-500">Review before printing or downloading</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={generatePDF} className="gap-1.5">
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
              <Button size="sm" onClick={handlePrint} className="gap-1.5 bg-[#009A44] hover:bg-[#008238] text-white">
                <Printer className="h-4 w-4" />
                Print
              </Button>
              <Button variant="ghost" size="sm" onClick={closePrintPreview} className="ml-2">
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-[800px] mx-auto my-6 shadow-lg">
              <div className="loan-review-print-area bg-white rounded-xl border overflow-hidden">
                {/* Cooperative Header */}
                <div className="flex items-center gap-4 p-5 sm:p-6 border-b border-slate-200 print-break-inside">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-50 border">
                    <img src={getCooperativeLogoSrc()} alt="Cooperative Logo" className="w-full h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; (e.target as HTMLImageElement).parentElement!.innerHTML = '<span class="text-xs font-bold text-green-700">CEIK</span>'; }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-slate-900">{orgInfo.name}</h3>
                    <p className="text-xs text-slate-500">Reg: {orgInfo.registrationNumber}</p>
                    <p className="text-xs text-slate-500">{orgInfo.address} | {orgInfo.phone} | {orgInfo.email}</p>
                  </div>
                </div>

                {/* Title */}
                <div className="text-center py-3 border-b border-slate-200 bg-slate-50/50">
                  <h2 className="text-lg font-bold text-[#009A44]">Loan Application Summary</h2>
                </div>

                {/* Application Reference */}
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 px-5 sm:px-6 py-4 text-sm border-b border-slate-200 bg-white">
                  <div><span className="text-slate-500">Application Number:</span> <span className="font-semibold text-slate-800">{applicationNumber}</span></div>
                  <div><span className="text-slate-500">Application Date:</span> <span className="font-semibold text-slate-800">{format(new Date(), "MMM dd, yyyy")}</span></div>
                  <div><span className="text-slate-500">Member ID:</span> <span className="font-semibold text-slate-800">{memberInfo.memberNumber}</span></div>
                  <div><span className="text-slate-500">Status:</span> <Badge variant="warning" className="text-[10px] px-2 py-0">Draft</Badge></div>
                </div>

                {/* Applicant Info */}
                <div className="print-break-inside">
                  <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#009A44]" /> Applicant Information
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 px-5 sm:px-6 py-4 text-sm">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0"><User className="h-4 w-4 text-[#009A44]" /></div>
                      <div className="min-w-0"><span className="text-slate-500">Full Name:</span> <span className="font-semibold text-slate-800 ml-1">{memberInfo.fullName}</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><BadgeCheck className="h-4 w-4 text-blue-600" /></div>
                      <div className="min-w-0"><span className="text-slate-500">Member Number:</span> <span className="font-semibold text-slate-800 ml-1">{memberInfo.memberNumber}</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-purple-50 flex items-center justify-center shrink-0"><Phone className="h-4 w-4 text-purple-600" /></div>
                      <div className="min-w-0"><span className="text-slate-500">Phone:</span> <span className="font-semibold text-slate-800 ml-1">{memberInfo.phone}</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><Mail className="h-4 w-4 text-amber-600" /></div>
                      <div className="min-w-0"><span className="text-slate-500">Email:</span> <span className="font-semibold text-slate-800 ml-1">{memberInfo.email}</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><Wallet className="h-4 w-4 text-emerald-600" /></div>
                      <div className="min-w-0"><span className="text-slate-500">Savings Balance:</span> <span className="font-semibold text-slate-800 ml-1">{formatCurrency(teacherSavings)}</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-cyan-50 flex items-center justify-center shrink-0"><TrendingUp className="h-4 w-4 text-cyan-600" /></div>
                      <div className="min-w-0"><span className="text-slate-500">Maximum Eligible Loan:</span> <span className="font-semibold text-slate-800 ml-1">{formatCurrency(maxLoanAmount)}</span></div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Loan Details */}
                <div className="print-break-inside">
                  <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-[#009A44]" /> Loan Details
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 px-5 sm:px-6 py-4 text-sm">
                    <div><span className="text-slate-500">Loan Type:</span> <span className="font-semibold text-slate-800 ml-1">{selectedLoanType?.name || "N/A"}</span></div>
                    <div><span className="text-slate-500">Interest Rate:</span> <span className="font-semibold text-slate-800 ml-1">{interestRate}%</span></div>
                    <div><span className="text-slate-500">Requested Amount:</span> <span className="font-semibold text-[#009A44] ml-1">{formatCurrency(numericAmount)}</span></div>
                    <div><span className="text-slate-500">Duration:</span> <span className="font-semibold text-slate-800 ml-1">{formatTerm(numericTerm)}</span></div>
                    <div className="sm:col-span-2"><span className="text-slate-500">Purpose:</span> <span className="font-semibold text-slate-800 ml-1">{purpose || "General"}</span></div>
                  </div>
                </div>

                <Separator />

                {/* Loan Breakdown — Prepaid Interest Model */}
                <div className="print-break-inside">
                  <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-[#009A44]" /> Loan Breakdown
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-5 sm:px-6 py-4">
                    <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                      <p className="text-xs text-slate-500 mb-1">Requested Amount</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(numericAmount)}</p>
                    </div>
                    <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
                      <p className="text-xs text-slate-500 mb-1">Interest Deducted Upfront ({interestRate}%)</p>
                      <p className="text-xl font-bold text-amber-700">-{formatCurrency(upfrontInterest)}</p>
                    </div>
                    <div className="bg-green-50/50 rounded-xl p-4 border border-green-100">
                      <p className="text-xs text-slate-500 mb-1">Net Amount You Receive</p>
                      <p className="text-xl font-bold text-green-700">{formatCurrency(netDisbursedAmount)}</p>
                    </div>
                    <div className="bg-purple-50/50 rounded-xl p-4 border border-purple-100">
                      <p className="text-xs text-slate-500 mb-1">Total Amount To Repay</p>
                      <p className="text-xl font-bold text-purple-700">{formatCurrency(totalRepayment)}</p>
                    </div>
                  </div>
                  <div className="px-5 sm:px-6 pb-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-sm text-slate-600">
                      For <strong>{formatCurrency(numericAmount)}</strong> at <strong>{interestRate}%</strong>, the cooperative deducts <strong>{formatCurrency(upfrontInterest)}</strong> upfront as interest. You receive <strong>{formatCurrency(netDisbursedAmount)}</strong> and repay <strong>{formatCurrency(totalRepayment)}</strong> in {formatTerm(numericTerm)}. Monthly installment: <strong>{formatCurrency(Math.round(monthlyInstallment))}</strong>.
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Repayment Schedule */}
                {repaymentSchedule.length > 0 && (
                  <div className="print-break-inside">
                    <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-[#009A44]" /> Repayment Schedule Preview
                      </h3>
                    </div>
                    <div className="px-5 sm:px-6 py-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 text-xs font-semibold text-slate-500">#</th>
                            <th className="text-left py-2 text-xs font-semibold text-slate-500">Due Date</th>
                            <th className="text-right py-2 text-xs font-semibold text-slate-500">Principal</th>
                            <th className="text-right py-2 text-xs font-semibold text-slate-500">Interest</th>
                            <th className="text-right py-2 text-xs font-semibold text-slate-500">Balance</th>
                            <th className="text-right py-2 text-xs font-semibold text-slate-500">Amount Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {repaymentSchedule.slice(0, 6).map((row) => (
                            <tr key={row.installment} className="border-b border-slate-100">
                              <td className="py-1.5 text-slate-600">{row.installment}</td>
                              <td className="py-1.5 text-slate-600">{row.dueDate}</td>
                              <td className="py-1.5 text-right text-slate-600">{Math.round(row.principal).toLocaleString()}</td>
                              <td className="py-1.5 text-right text-slate-600">{Math.round(row.interest).toLocaleString()}</td>
                              <td className="py-1.5 text-right text-slate-600">{Math.round(row.balance).toLocaleString()}</td>
                              <td className="py-1.5 text-right font-medium text-slate-800">{Math.round(row.amountDue).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {repaymentSchedule.length > 6 && (
                        <p className="text-xs text-slate-400 mt-2 italic">...and {repaymentSchedule.length - 6} more installments</p>
                      )}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Eligibility */}
                <div className="print-break-inside">
                  <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[#009A44]" /> Eligibility Verification
                    </h3>
                  </div>
                  <div className="px-5 sm:px-6 py-4 space-y-2">
                    {[
                      { label: "Loan amount within limit", ok: eligibilityItems.amountWithinLimit },
                      { label: "Savings requirement satisfied", ok: eligibilityItems.savingsRequirement },
                      { label: "Membership duration satisfied", ok: eligibilityItems.membershipDuration },
                      { label: "Required shares satisfied", ok: eligibilityItems.sharesSatisfied },
                      { label: "Loan policy satisfied", ok: eligibilityItems.loanPolicySatisfied },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        {item.ok ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" />
                        )}
                        <span className={`text-sm ${item.ok ? "text-green-700" : "text-orange-700"}`}>{item.label}</span>
                        {!item.ok && <Badge variant="warning" className="text-[9px] px-1.5 py-0 capitalize">Required</Badge>}
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Loan Terms */}
                <div className="print-break-inside">
                  <div className="px-5 sm:px-6 py-4 bg-amber-50/40 border-b border-amber-100">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
                      <Info className="h-4 w-4 text-amber-600" /> Important Loan Terms
                    </h3>
                    <ul className="space-y-1.5">
                      {["Late repayment penalties apply as per cooperative policy.", "Processing fee is non-refundable once the application is processed.", "Insurance policy covers the outstanding loan balance in case of unforeseen events.", "Loan approval depends on committee review and credit assessment.", "Loan disbursement is subject to verification of provided information.", "Providing false information may lead to rejection and disciplinary action."].map((term, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-slate-700">
                          <span className="text-amber-600 mt-0.5 shrink-0">&bull;</span> {term}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <Separator />

                {/* Declaration */}
                <div className="print-break-inside px-5 sm:px-6 py-5">
                  <div className="mx-0 my-0 border-2 border-green-200 rounded-lg bg-green-50/50 p-4 sm:p-5">
                    <p className="text-sm text-slate-700 leading-relaxed">
                      <span className="font-semibold text-slate-800">I certify</span> that the information provided in this application is accurate and complete. I understand that providing false information may result in rejection of my application and other actions according to cooperative policies.
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Signature */}
                <div className="print-break-inside px-5 sm:px-6 py-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-6">
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-1">Applicant Signature</p>
                      <div className="border-b border-slate-300 pb-1 mb-4"><span className="text-slate-400 text-sm italic">_________________________</span></div>
                      <p className="text-sm font-semibold text-slate-700 mb-1">Date</p>
                      <div className="border-b border-slate-300 pb-1"><span className="text-slate-400 text-sm italic">{format(new Date(), "MMM dd, yyyy")} _________________________</span></div>
                    </div>
                  </div>
                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider text-center mb-4">Approval Section — Reserved for Cooperative Use</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                      {["Loan Officer", "Committee", "Treasurer", "Manager"].map((role) => (
                        <div key={role} className="text-center">
                          <p className="font-semibold text-slate-700 mb-2">{role}</p>
                          <div className="border-b border-slate-300 mb-1 pb-1"><span className="text-slate-300">___________________</span></div>
                          <p className="text-slate-400">Signature</p>
                          <div className="border-b border-slate-300 mb-1 pb-1 mt-2"><span className="text-slate-300">___________________</span></div>
                          <p className="text-slate-400">Date</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-slate-200 px-5 sm:px-6 py-2 text-[10px] text-slate-400 flex justify-between">
                  <span>Generated by Cooperative Management System</span>
                  <span>Page 1 of 1</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <button onClick={() => step > 1 && step < 4 ? setStep((step - 1) as Step) : navigate("/member/loans")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2">
          <ArrowLeft className="h-4 w-4" />
          {step === 1 ? "Back to Loans" : "Previous step"}
        </button>
        <h1 className="text-xl font-bold text-slate-900">Apply for Loan</h1>
        <p className="text-sm text-slate-500">Fill in your details to apply for a new loan</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
              step === s ? "bg-[#009A44] text-white" :
              step > s ? "bg-green-100 text-[#009A44]" :
              "bg-slate-100 text-slate-400"
            }`}>
              {step > s ? <CheckCircle2 className="h-4 w-4" /> : s}
            </div>
            <div className={`h-0.5 flex-1 ${step > s ? "bg-[#009A44]" : "bg-slate-200"}`} />
          </div>
        ))}
      </div>

      {/* Step 1: Loan Details */}
      {step === 1 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-slate-900 mb-1">Loan Details</h2>
              <p className="text-xs text-slate-500">Tell us about the loan you need</p>
            </div>

            {/* Savings & Loan Eligibility */}
            {contributionsLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                <Loader className="h-4 w-4 animate-spin" />
                Loading member savings...
              </div>
            ) : (
              <div className={`border rounded-lg p-3 space-y-1.5 ${isBlocked ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                <div className="flex justify-between text-sm">
                  <span className={isBlocked ? "text-red-800" : "text-green-800"}>Current Savings:</span>
                  <span className={`font-semibold ${isBlocked ? "text-red-900" : "text-green-900"}`}>{formatCurrency(teacherSavings)}</span>
                </div>
                {pendingApplicationCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-800 font-medium">Pending Applications:</span>
                    <span className="font-semibold text-amber-600">{pendingApplicationCount}</span>
                  </div>
                )}
                {outstandingLoanBalance > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-red-800 font-medium">Active Loan Balance:</span>
                    <span className="font-semibold text-red-600">{formatCurrency(outstandingLoanBalance)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className={isBlocked ? "text-red-800" : "text-green-800"}>Eligible Savings:</span>
                  <span className={`font-semibold ${isBlocked ? "text-red-900" : "text-green-900"}`}>{formatCurrency(eligibleSavings)}</span>
                </div>
                <Separator className={`my-1.5 ${isBlocked ? "bg-red-200" : ""}`} />
                <div className="flex justify-between text-sm">
                  <span className={isBlocked ? "text-red-800" : "text-green-800"}>Maximum Loan ({maxLoanMultiplier}x):</span>
                  <span className={`font-semibold ${isBlocked ? "text-red-900" : "text-green-900"}`}>{formatCurrency(maxLoanAmount)}</span>
                </div>
                {isBlockedByPending && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-100 p-2 rounded mt-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span className="font-medium">You already have a pending loan application awaiting approval.</span>
                  </div>
                )}
                {outstandingLoanBalance > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-100 p-2 rounded mt-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span className="font-medium">You have an active loan balance of {formatCurrency(outstandingLoanBalance)}. You cannot apply for a new loan until the balance is cleared.</span>
                  </div>
                )}
                {teacherSavings === 0 && outstandingLoanBalance === 0 && pendingApplicationCount === 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded mt-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>You have no eligible savings for a loan. Build savings first.</span>
                  </div>
                )}
              </div>
            )}

            {/* Loan Type Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Loan Type</label>
              {loanTypesLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader className="h-4 w-4 animate-spin" />
                  Loading loan types...
                </div>
              ) : (loanTypes || []).length === 0 ? (
                <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  <span>No active loan types found. Please configure a loan type in settings.</span>
                </div>
              ) : (
                <>
                  <Select value={loanTypeId} onValueChange={(val) => { setLoanTypeId(val); }}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select loan type" />
                    </SelectTrigger>
                    <SelectContent>
                      {displayLoanTypes.map((lt: any) => {
                        const ltMax = lt.maxAmount ? Number(lt.maxAmount) : null;
                        const cls = finalClassifiedLoanTypes.find((c) => String(c.loanType.id) === String(lt.id));
                        const isRecommended = cls?.category === "recommended";
                        const isNotEligible = cls?.category === "not_eligible";
                        return (
                          <SelectItem key={lt.id} value={String(lt.id)} disabled={isNotEligible}>
                            <div className="flex flex-col py-0.5">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{lt.displayName || lt.name}</span>
                                {isRecommended && (
                                  <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-1.5 py-0.5 text-[9px] font-semibold leading-none">
                                    Recommended
                                  </span>
                                )}
                                {isNotEligible && (
                                  <span className="inline-flex items-center rounded-full bg-red-100 text-red-600 px-1.5 py-0.5 text-[9px] font-semibold leading-none">
                                    Not eligible
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                                <span>{Number(lt.interestRate || 0)}% interest</span>
                                {ltMax && <span>• Up to {formatCurrency(ltMax)}</span>}
                                {lt.maxTermMonths && <span>• {lt.maxTermMonths} mo</span>}
                              </div>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {!isBlocked && eligibleLoanTypes.length === 0 && loanTypeId === "0" && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded mt-1.5">
                      <Info className="h-3.5 w-3.5 shrink-0" />
                      <span>You are not eligible for any loan type based on current savings ({formatCurrency(teacherSavings)}). Maximum eligible loan: {formatCurrency(maxLoanAmount)}.</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Available Loan Options — Card Comparison */}
            {!loanTypesLoading && finalClassifiedLoanTypes.length > 0 && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700">Available Loan Options</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {finalClassifiedLoanTypes.map((classified) => {
                    const lt = classified.loanType;
                    const isSelected = String(lt.id) === loanTypeId;
                    const isRecommended = classified.category === "recommended";
                    const isDisabled = classified.category === "not_eligible";
                    const ltMin = Number(lt.minAmount) || 0;
                    const ltMax = lt.maxAmount ? Number(lt.maxAmount) : null;
                    const ltInterest = Number(lt.interestRate) || 0;

                    return (
                      <button
                        key={lt.id}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          if (!isDisabled) {
                            setLoanTypeId(String(lt.id));
                            if (lt.defaultTermMonths) setTermMonths(String(lt.defaultTermMonths));
                          }
                        }}
                        className={cn(
                          "relative text-left rounded-lg border p-4 transition-all",
                          isSelected && !isDisabled
                            ? "border-[#009A44] bg-green-50 ring-1 ring-[#009A44]"
                            : isDisabled
                            ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 cursor-pointer"
                        )}
                      >
                        {isRecommended && (
                          <span className="absolute -top-2.5 left-3 inline-flex items-center rounded-full bg-[#009A44] text-white px-2 py-0.5 text-[10px] font-semibold leading-none shadow-sm">
                            ★ Recommended
                          </span>
                        )}
                        {isSelected && !isDisabled && (
                          <div className="absolute top-3 right-3">
                            <CheckCircle2 className="h-5 w-5 text-[#009A44]" />
                          </div>
                        )}
                        <p className={cn("text-sm font-semibold", isDisabled ? "text-slate-500" : "text-slate-900")}>
                          {lt.displayName || lt.name}
                        </p>
                        <p className={cn("text-lg font-bold mt-1", isDisabled ? "text-slate-400" : "text-[#009A44]")}>
                          {ltInterest}% <span className="text-xs font-normal text-slate-500">interest</span>
                        </p>
                        <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                          <p>
                            Min: {formatCurrency(ltMin)}
                            {ltMax && ` · Max: ${formatCurrency(ltMax)}`}
                          </p>
                          {lt.maxTermMonths && <p>Up to {formatTerm(lt.maxTermMonths)}</p>}
                          {lt.defaultTermMonths && <p>Default: {formatTerm(lt.defaultTermMonths)}</p>}
                        </div>
                        {isDisabled && classified.notEligibleReasons.length > 0 && (
                          <div className="mt-2 text-[11px] text-red-500 space-y-0.5">
                            {classified.notEligibleReasons.map((reason, i) => (
                              <p key={i} className="flex items-start gap-1">
                                <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                {reason}
                              </p>
                            ))}
                          </div>
                        )}
                        {!isDisabled && (lt.requiredInsurerCount || lt.requiresInsurers) && (
                          <p className="mt-2 text-[11px] text-slate-400">
                            Requires {lt.requiredInsurerCount || 1} guarantor{lt.requiredInsurerCount !== 1 ? "s" : ""}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selected Loan Type Details Card */}
            {selectedLoanType && (
              <div className="border border-green-200 rounded-lg overflow-hidden">
                <div className="bg-green-50 px-4 py-3 border-b border-green-100 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-[#009A44]" />
                  <span className="font-semibold text-slate-900">{selectedLoanType.name}</span>
                  {recommendedClassification?.loanType.id === selectedLoanType.id && (
                    <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-[10px] font-semibold">
                      Recommended
                    </span>
                  )}
                  {selectedLoanType.description && (
                    <span className="text-xs text-slate-500 ml-1">- {selectedLoanType.description}</span>
                  )}
                </div>
                <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Interest Rate</p>
                    <p className="text-sm font-semibold text-slate-900">{interestRate}%</p>
                  </div>
                  {selectedLoanType.minAmount > 0 && (
                    <div>
                      <p className="text-xs text-slate-500">Minimum Amount</p>
                      <p className="text-sm font-semibold text-slate-900">{formatCurrency(selectedLoanType.minAmount)}</p>
                    </div>
                  )}
                  {selectedLoanType.maxAmount && (
                    <div>
                      <p className="text-xs text-slate-500">Maximum Amount</p>
                      <p className="text-sm font-semibold text-slate-900">{formatCurrency(effectiveMax)}</p>
                    </div>
                  )}
                  {selectedLoanType.maxTermMonths && (
                    <div>
                      <p className="text-xs text-slate-500">Maximum Term</p>
                      <p className="text-sm font-semibold text-slate-900">{formatTerm(selectedLoanType.maxTermMonths)}</p>
                    </div>
                  )}
                  {(selectedLoanType.requiredInsurerCount || selectedLoanType.requiresInsurers) && (
                    <div>
                      <p className="text-xs text-slate-500">Required Guarantors</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedLoanType.requiredInsurerCount || 1} {selectedLoanType.requiredInsurerCount === 1 ? "person" : "people"}
                      </p>
                    </div>
                  )}
                  {selectedLoanType.defaultTermMonths && (
                    <div>
                      <p className="text-xs text-slate-500">Suggested Term</p>
                      <p className="text-sm font-semibold text-slate-900">{formatTerm(selectedLoanType.defaultTermMonths)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Loan Explanation */}
            {selectedLoanType && (
              <div className={`border rounded-lg p-3 ${isBlocked ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}>
                <div className="flex items-start gap-2">
                  {isBlocked ? (
                    <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  ) : (
                    <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${isBlocked ? "text-red-800" : "text-blue-800"}`}>
                      {isBlocked ? "Loan not available" : "How it works"}
                    </p>
                    <p className={`text-xs mt-1 ${isBlocked ? "text-red-700" : "text-blue-700"}`}>
                      {isBlockedByOutstanding
                        ? `You have an active loan balance of ${formatCurrency(outstandingLoanBalance)}. You are not eligible for a new loan until the balance is cleared.`
                        : isBlockedByPending
                        ? `You already have a pending loan application awaiting approval. Please wait for approval or rejection before applying again.`
                        : `Based on your current savings of ${formatCurrency(teacherSavings)}, you can borrow up to ${formatCurrency(maxLoanAmount)} (${maxLoanMultiplier}x your savings).${selectedLoanType.description ? ` ${selectedLoanType.description}` : ""} Interest of ${interestRate}% is deducted upfront. You receive the net amount and repay the full principal in monthly installments.`
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Eligibility Checks */}
            {loanTypeSelected && (
              <div className="border border-slate-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-slate-700 mb-2">Eligibility Checks</p>
                <div className="flex items-center gap-2">
                  {hasSavings ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                  )}
                  <span className={`text-xs ${hasSavings ? "text-green-700" : "text-red-700"}`}>
                    Active savings balance ({formatCurrency(eligibleSavings)} eligible)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {!isBlocked ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                  )}
                  <span className={`text-xs ${!isBlocked ? "text-green-700" : "text-red-700"}`}>
                    {isBlockedByOutstanding
                      ? `Active loan balance: ${formatCurrency(outstandingLoanBalance)}. Cannot apply until cleared.`
                      : isBlockedByPending
                      ? "You have a pending loan application awaiting approval."
                      : isBlocked
                      ? "Active loan limit reached. Only one active loan allowed."
                      : "No active loan restrictions"}
                  </span>
                </div>
                {selectedLoanType && (
                  <div className="flex items-center gap-2">
                    {numericAmount > 0 && amountValid ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-slate-400 shrink-0" />
                    )}
                    <span className={`text-xs ${numericAmount > 0 && amountValid ? "text-green-700" : "text-slate-500"}`}>
                      {numericAmount > 0
                        ? `Amount between ${formatCurrency(effectiveMin)} and ${formatCurrency(effectiveMax)}`
                        : `Allowed range: ${formatCurrency(effectiveMin)} - ${formatCurrency(effectiveMax)}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Loan Amount */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Loan Amount (FRW)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  placeholder="e.g. 500000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-9"
                  min="1000"
                  disabled={isBlocked}
                />
              </div>
              {isBlocked ? (
                <p className="text-xs text-red-500 mt-1 font-medium">
                  {isBlockedByOutstanding
                    ? "You have an active loan balance. Please clear it before applying for a new loan."
                    : "You have reached the active loan limit. Only one active loan is allowed."}
                </p>
              ) : isBlockedByPending ? (
                <p className="text-xs text-amber-600 mt-1 font-medium">
                  You have {pendingApplicationCount} pending loan application(s). A new application will be submitted.
                </p>
              ) : selectedLoanType ? (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-500">
                    Min: {formatCurrency(selectedLoanType.minAmount || 1000)}
                  </p>
                  <p className="text-xs text-slate-500">
                    Max: {formatCurrency(effectiveMax)}
                  </p>
                </div>
              ) : null}
              {numericAmount > 0 && (
                <>
                  {numericAmount < effectiveMin && (
                    <p className="text-xs text-red-500 mt-1">
                      Minimum loan amount is {formatCurrency(effectiveMin)}
                    </p>
                  )}
                  {numericAmount > maxLoanAmount && (
                    <p className="text-xs text-red-500 mt-1">
                      Amount exceeds maximum loan of {formatCurrency(maxLoanAmount)}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Estimated Repayment Preview — Prepaid Interest */}
            {numericAmount > 0 && selectedLoanType && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-[#009A44]" />
                  Loan Breakdown
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Interest Rate</p>
                    <p className="text-sm font-semibold text-slate-900">{interestRate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Interest Deducted Upfront</p>
                    <p className="text-sm font-semibold text-amber-700">{formatCurrency(upfrontInterest)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Net Amount You Receive</p>
                    <p className="text-sm font-semibold text-green-700">{formatCurrency(netDisbursedAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Amount To Repay</p>
                    <p className="text-sm font-semibold text-slate-900">{formatCurrency(totalRepayment)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Est. Monthly Payment</p>
                    <p className="text-sm font-semibold text-[#009A44]">{formatCurrency(Math.round(monthlyInstallment))}</p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Interest is deducted upfront. For {formatCurrency(numericAmount)} at {interestRate}%, the cooperative deducts {formatCurrency(upfrontInterest)}. You receive {formatCurrency(netDisbursedAmount)} and repay {formatCurrency(totalRepayment)} over {formatTerm(numericTerm)}.
                </p>
              </div>
            )}

            {/* Purpose */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Purpose of Loan</label>
              <Textarea
                placeholder="e.g. School fees, business investment, home improvement..."
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                rows={3}
              />
            </div>

            {/* Warnings */}
            {!loanTypeSelected && (loanTypes || []).length > 0 && (
              <p className="text-xs text-amber-600">Please select a loan type to continue.</p>
            )}
            {loanTypeSelected && teacherSavings === 0 && outstandingLoanBalance === 0 && (
              <p className="text-xs text-red-500">You have no eligible savings for a loan.</p>
            )}
            {loanTypeSelected && outstandingLoanBalance > 0 && (
              <p className="text-xs text-red-500 font-medium">You have an active loan balance of {formatCurrency(outstandingLoanBalance)}. Cannot apply for a new loan until cleared.</p>
            )}
            {loanTypeSelected && pendingApplicationCount > 0 && outstandingLoanBalance === 0 && (
              <p className="text-xs text-amber-500 font-medium">You have {pendingApplicationCount} pending loan application(s) awaiting approval.</p>
            )}
            {loanTypeSelected && hasActiveLoan && !outstandingLoanBalance && (
              <p className="text-xs text-red-500">You have reached the active loan limit. Only one active loan is allowed at a time.</p>
            )}

            {/* Guarantors */}
            {selectedLoanType && (
              <GuarantorFields
                guarantors={guarantors}
                setGuarantors={setGuarantors}
                requiredCount={selectedLoanType.requiredInsurerCount || 0}
                maxCount={Math.max(selectedLoanType.requiredInsurerCount || 3, 3)}
                isAdmin={false}
              />
            )}

            {/* Continue Button */}
            <Button
              className="w-full bg-[#009A44] hover:bg-[#008238] text-white"
              disabled={!canNextStep1}
              onClick={() => setStep(2)}
            >
              Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Repayment Plan */}
      {step === 2 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-slate-900 mb-1">Repayment Plan</h2>
              <p className="text-xs text-slate-500">Choose your repayment duration</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Duration (months)</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  placeholder="e.g. 12"
                  value={termMonths}
                  onChange={(e) => setTermMonths(e.target.value)}
                  className="pl-9"
                  min="1"
                  max="60"
                />
              </div>
              {selectedLoanType?.maxTermMonths && (
                <p className="text-xs text-slate-500 mt-1">
                  Maximum term for this loan type: {formatTerm(selectedLoanType.maxTermMonths)}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">Choose between 1 and 60 months</p>
            </div>

            {/* Installment Preview */}
            {numericAmount > 0 && numericTerm > 0 && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-slate-700">Loan Breakdown</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Requested Amount</p>
                    <p className="text-sm font-semibold">{formatCurrency(numericAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Interest Rate</p>
                    <p className="text-sm font-semibold">{interestRate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Interest Deducted Upfront</p>
                    <p className="text-sm font-semibold text-amber-700">-{formatCurrency(upfrontInterest)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Net Amount You Receive</p>
                    <p className="text-sm font-semibold text-green-700">{formatCurrency(netDisbursedAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Duration</p>
                    <p className="text-sm font-semibold">{numericTerm} months</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Monthly Payment</p>
                    <p className="text-sm font-semibold text-[#009A44]">{formatCurrency(Math.round(monthlyInstallment))}</p>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-500">Total Amount To Repay</span>
                    <span className="text-sm font-bold">{formatCurrency(totalRepayment)}</span>
                  </div>
                </div>
              </div>
            )}

            <Button
              className="w-full bg-[#009A44] hover:bg-[#008238] text-white"
              disabled={!canNextStep2}
              onClick={() => setStep(3)}
            >
              Review Application
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review & Confirm - Professional Banking SACCO Layout */}
      {step === 3 && (
        <div ref={printRef} className="loan-review-print-area">
          {memberProfileLoading ? (
            <div className="bg-white rounded-xl border shadow-sm p-8 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-slate-100 animate-pulse" />
                <div className="space-y-2 flex-1">
                  <div className="h-5 bg-slate-100 rounded animate-pulse w-64" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-48" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-80" />
                </div>
              </div>
              <div className="h-10 bg-slate-100 rounded animate-pulse w-full" />
              <div className="grid grid-cols-2 gap-4">
                <div className="h-16 bg-slate-100 rounded animate-pulse" />
                <div className="h-16 bg-slate-100 rounded animate-pulse" />
                <div className="h-16 bg-slate-100 rounded animate-pulse" />
                <div className="h-16 bg-slate-100 rounded animate-pulse" />
              </div>
              <div className="h-24 bg-slate-100 rounded animate-pulse w-full" />
              <div className="h-24 bg-slate-100 rounded animate-pulse w-full" />
              <div className="h-40 bg-slate-100 rounded animate-pulse w-full" />
            </div>
          ) : (
          <>
          {/* ─── Screen Header ─── */}
          <div className="print:hidden mb-4">
            <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2">
              <ArrowLeft className="h-4 w-4" />
              Previous step
            </button>
          </div>

          <div className="print:hidden flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Review Loan Application</h1>
              <p className="text-sm text-slate-500">Please review your application carefully before submitting it for approval.</p>
            </div>
            <Badge variant="warning" className="text-sm px-3 py-1 w-fit">
              <Clock className="h-3.5 w-3.5 mr-1 inline" />
              Draft
            </Badge>
          </div>

          {/* ─── Top Action Buttons (Screen only) ─── */}
          <div className="print:hidden flex flex-wrap gap-2 mb-6">
            <Button variant="outline" size="sm" onClick={generatePDF} disabled={submitting} className="gap-1.5">
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
            <Button variant="outline" size="sm" onClick={openPrintPreview} className="gap-1.5">
              <Printer className="h-4 w-4" />
              Print Preview
            </Button>
            <Button
              size="sm"
              className="bg-[#009A44] hover:bg-[#008238] text-white ml-auto gap-1.5"
              disabled={submitting || !declarationChecked}
              onClick={handleSubmit}
            >
              {submitting ? (
                <><Loader className="animate-spin h-4 w-4" /> Submitting...</>
              ) : (
                <><ShieldCheck className="h-4 w-4" /> Submit Application</>
              )}
            </Button>
          </div>

          {/* ─── Main Application Document ─── */}
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            {/* ── Cooperative Header ── */}
            <div className="flex items-center gap-4 p-5 sm:p-6 border-b border-slate-200 print-break-inside">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-50 border">
                <img
                  src={getCooperativeLogoSrc()}
                  alt="Cooperative Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                    (e.target as HTMLImageElement).parentElement!.innerHTML = '<span class="text-xs font-bold text-green-700">CEIK</span>';
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-slate-900">{orgInfo.name}</h3>
                <p className="text-xs text-slate-500">Reg: {orgInfo.registrationNumber}</p>
                <p className="text-xs text-slate-500">{orgInfo.address} | {orgInfo.phone} | {orgInfo.email}</p>
              </div>
            </div>

            {/* ── Title ── */}
            <div className="text-center py-3 border-b border-slate-200 bg-slate-50/50">
              <h2 className="text-lg font-bold text-[#009A44]">Loan Application Summary</h2>
            </div>

            {/* ── Application Reference ── */}
            <div className="grid grid-cols-2 gap-y-2 gap-x-4 px-5 sm:px-6 py-4 text-sm border-b border-slate-200 bg-white">
              <div><span className="text-slate-500">Application Number:</span> <span className="font-semibold text-slate-800">{applicationNumber}</span></div>
              <div><span className="text-slate-500">Application Date:</span> <span className="font-semibold text-slate-800">{format(new Date(), "MMM dd, yyyy")}</span></div>
              <div><span className="text-slate-500">Member ID:</span> <span className="font-semibold text-slate-800">{memberInfo.memberNumber}</span></div>
              <div><span className="text-slate-500">Status:</span> <Badge variant="warning" className="text-[10px] px-2 py-0">Draft</Badge></div>
            </div>

            {/* ── Applicant Information Card ── */}
            <div className="print-break-inside">
              <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#009A44]" />
                  Applicant Information
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 px-5 sm:px-6 py-4 text-sm">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-[#009A44]" />
                  </div>
                  <div className="min-w-0"><span className="text-slate-500">Full Name:</span> <span className="font-semibold text-slate-800 ml-1">{memberInfo.fullName}</span></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <BadgeCheck className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0"><span className="text-slate-500">Member Number:</span> <span className="font-semibold text-slate-800 ml-1">{memberInfo.memberNumber}</span></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                    <Phone className="h-4 w-4 text-[#009A44]" />
                  </div>
                  <div className="min-w-0"><span className="text-slate-500">Phone Number:</span> <span className="font-semibold text-slate-800 ml-1">{memberInfo.phone}</span></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Mail className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0"><span className="text-slate-500">Email:</span> <span className="font-semibold text-slate-800 ml-1">{memberInfo.email}</span></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                    <Wallet className="h-4 w-4 text-[#009A44]" />
                  </div>
                  <div className="min-w-0"><span className="text-slate-500">Savings Balance:</span> <span className="font-semibold text-[#009A44] ml-1">{formatCurrency(teacherSavings)}</span></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0"><span className="text-slate-500">Maximum Eligible Loan:</span> <span className="font-semibold text-slate-800 ml-1">{formatCurrency(maxLoanAmount)}</span></div>
                </div>
              </div>
              {teacherSavings > 0 && (
                <div className="px-5 sm:px-6 pb-4">
                  <Badge variant="success" className="text-[10px]">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Eligible for Application
                  </Badge>
                </div>
              )}
            </div>

            <Separator />

            {/* ── Loan Details Card ── */}
            <div className="print-break-inside">
              <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-[#009A44]" />
                  Loan Details
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 px-5 sm:px-6 py-4 text-sm">
                <div><span className="text-slate-500">Loan Type:</span> <span className="font-semibold text-slate-800 ml-1">{selectedLoanType?.displayName || selectedLoanType?.name || "N/A"}</span></div>
                <div><span className="text-slate-500">Loan Category:</span> <span className="font-semibold text-slate-800 ml-1">{selectedLoanType?.category || selectedLoanType?.loanCategory || "Standard"}</span></div>
                <div><span className="text-slate-500">Requested Amount:</span> <span className="font-semibold text-slate-800 ml-1">{formatCurrency(numericAmount)}</span></div>
                <div><span className="text-slate-500">Purpose:</span> <span className="font-semibold text-slate-800 ml-1">{purpose || "General"}</span></div>
                <div><span className="text-slate-500">Interest Rate:</span> <span className="font-semibold text-slate-800 ml-1">{interestRate}%</span></div>
                <div><span className="text-slate-500">Interest Calculation:</span> <span className="font-semibold text-slate-800 ml-1">{selectedLoanType?.interestType || "Simple Interest"}</span></div>
                <div><span className="text-slate-500">Loan Duration:</span> <span className="font-semibold text-slate-800 ml-1">{formatTerm(numericTerm)}</span></div>
                <div><span className="text-slate-500">Processing Fee:</span> <span className="font-semibold text-slate-800 ml-1">{formatCurrency(processingFee)}</span></div>
                <div><span className="text-slate-500">Insurance Fee:</span> <span className="font-semibold text-slate-800 ml-1">{formatCurrency(insuranceFee)}</span></div>
                <div><span className="text-slate-500">Required Shares:</span> <span className="font-semibold text-slate-800 ml-1">{selectedLoanType?.requiredShares || groupSettings?.minSharesRequired || "N/A"}</span></div>
                <div><span className="text-slate-500">Grace Period:</span> <span className="font-semibold text-slate-800 ml-1">{selectedLoanType?.gracePeriodDays || groupSettings?.contributionGracePeriodDays || 0} days</span></div>
              </div>
              {guarantors.length > 0 && (
                <div className="border-t border-slate-200 px-5 sm:px-6 py-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Selected Guarantors</p>
                  <div className="space-y-1.5">
                    {guarantors.map((g) => (
                      <div key={g.id} className="flex items-center gap-2 text-sm">
                        <Users className="h-3.5 w-3.5 text-green-500 shrink-0" />
                        <span className="font-medium text-slate-800">{g.name}</span>
                        {g.memberCode && <span className="text-slate-400">({g.memberCode})</span>}
                        {g.savings > 0 && <span className="text-xs text-green-600">{formatCurrency(g.savings)} savings</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* ── Loan Breakdown Card ── */}
            <div className="print-break-inside">
              <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-[#009A44]" />
                  Loan Breakdown
                </h3>
              </div>
              <div className="px-5 sm:px-6 py-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-blue-50/50 rounded-xl p-3 border border-blue-100">
                    <p className="text-xs text-slate-500">Requested Amount</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(numericAmount)}</p>
                  </div>
                  <div className="bg-amber-50/50 rounded-xl p-3 border border-amber-100">
                    <p className="text-xs text-slate-500">Interest Deducted Upfront ({interestRate}%)</p>
                    <p className="text-lg font-bold text-amber-700">-{formatCurrency(upfrontInterest)}</p>
                  </div>
                  <div className="bg-green-50/50 rounded-xl p-3 border border-green-100">
                    <p className="text-xs text-slate-500">Net Amount You Receive</p>
                    <p className="text-lg font-bold text-green-700">{formatCurrency(netDisbursedAmount)}</p>
                  </div>
                  <div className="bg-purple-50/50 rounded-xl p-3 border border-purple-100">
                    <p className="text-xs text-slate-500">Total Amount To Repay</p>
                    <p className="text-lg font-bold text-purple-700">{formatCurrency(totalRepayment)}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Processing Fee (1%)</span>
                  <span className="text-sm font-semibold text-slate-800">{formatCurrency(processingFee)}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Insurance Fee (0.5%)</span>
                  <span className="text-sm font-semibold text-slate-800">{formatCurrency(insuranceFee)}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Other Charges</span>
                  <span className="text-sm font-semibold text-slate-800">{formatCurrency(otherCharges)}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                  <div className="bg-green-50 rounded-lg border border-green-200 p-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Monthly Installment</p>
                    <p className="text-lg font-bold text-[#009A44]">{formatCurrency(Math.round(monthlyInstallment))}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg border border-green-200 p-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Total Amount To Repay</p>
                    <p className="text-lg font-bold text-[#009A44]">{formatCurrency(totalRepayment)}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg border border-green-200 p-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Net You Receive</p>
                    <p className="text-lg font-bold text-[#009A44]">{formatCurrency(netDisbursedAmount)}</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* ── Repayment Schedule Preview ── */}
            <div className="print-break-inside">
              <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#009A44]" />
                  Repayment Schedule Preview
                </h3>
              </div>
              <div className="px-5 sm:px-6 py-4 overflow-x-auto">
                <table className="w-full text-xs sm:text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-2 px-2 font-semibold text-slate-600">#</th>
                      <th className="text-left py-2 px-2 font-semibold text-slate-600">Due Date</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Principal</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Interest</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Balance</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Amount Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repaymentSchedule.slice(0, Math.min(6, repaymentSchedule.length)).map((row) => (
                      <tr key={row.installment} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                        <td className="py-2 px-2 text-slate-600">{row.installment}</td>
                        <td className="py-2 px-2 text-slate-700 font-medium">{row.dueDate}</td>
                        <td className="py-2 px-2 text-right text-slate-700">{Math.round(row.principal).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right text-slate-700">{Math.round(row.interest).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right font-medium text-slate-700">{Math.round(row.balance).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right font-semibold text-[#009A44]">{Math.round(row.amountDue).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {repaymentSchedule.length > 6 && (
                  <p className="text-xs text-slate-400 text-center mt-3 italic">
                    ...remaining {repaymentSchedule.length - 6} repayments continue until loan maturity.
                  </p>
                )}
              </div>
            </div>

            <Separator />

            {/* ── Eligibility Summary ── */}
            <div className="print-break-inside">
              <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#009A44]" />
                  Eligibility Verification
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 px-5 sm:px-6 py-4 text-sm">
                {[
                  { label: "Loan amount within limit", ok: eligibilityItems.amountWithinLimit },
                  { label: "Savings requirement satisfied", ok: eligibilityItems.savingsRequirement },
                  { label: "Membership duration satisfied", ok: eligibilityItems.membershipDuration },
                  { label: "Required shares satisfied", ok: eligibilityItems.sharesSatisfied },
                  { label: "Loan policy satisfied", ok: eligibilityItems.loanPolicySatisfied },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {item.ok ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" />
                    )}
                    <span className={`text-sm ${item.ok ? "text-green-700" : "text-orange-700"}`}>
                      {item.label}
                    </span>
                    {!item.ok && (
                      <Badge variant="warning" className="text-[9px] px-1.5 py-0 capitalize">Required</Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* ── Important Loan Terms ── */}
            <div className="print-break-inside">
              <div className="px-5 sm:px-6 py-4 bg-amber-50/40 border-b border-amber-100">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
                  <Info className="h-4 w-4 text-amber-600" />
                  Important Loan Terms
                </h3>
                <ul className="space-y-1.5">
                  {[
                    "Late repayment penalties apply as per cooperative policy.",
                    "Processing fee is non-refundable once the application is processed.",
                    "Insurance policy covers the outstanding loan balance in case of unforeseen events.",
                    "Loan approval depends on committee review and credit assessment.",
                    "Loan disbursement is subject to verification of provided information.",
                    "Providing false information may lead to rejection and disciplinary action.",
                  ].map((term, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-slate-700">
                      <span className="text-amber-600 mt-0.5 shrink-0">•</span>
                      {term}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <Separator />

            {/* ── Member Declaration ── */}
            <div className="print-break-inside">
              <div className="mx-5 sm:mx-6 my-4 border-2 border-green-200 rounded-lg bg-green-50/50 p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="declaration"
                    checked={declarationChecked}
                    onCheckedChange={(val) => setDeclarationChecked(val === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="declaration" className="text-sm text-slate-700 leading-relaxed cursor-pointer select-none">
                    <span className="font-semibold text-slate-800">I certify</span> that the information provided in this application is accurate and complete. I understand that providing false information may result in rejection of my application and other actions according to cooperative policies.
                  </label>
                </div>
              </div>
            </div>

            <Separator />

            {/* ── Signature Section ── */}
            <div className="print-break-inside px-5 sm:px-6 py-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-6">
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1">Applicant Signature</p>
                  <div className="border-b border-slate-300 pb-1 mb-4">
                    <span className="text-slate-400 text-sm italic">_________________________</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-700 mb-1">Date</p>
                  <div className="border-b border-slate-300 pb-1">
                    <span className="text-slate-400 text-sm italic">{format(new Date(), "MMM dd, yyyy")} _________________________</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 mt-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider text-center mb-4">Approval Section — Reserved for Cooperative Use</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                  {["Loan Officer", "Committee", "Treasurer", "Manager"].map((role) => (
                    <div key={role} className="text-center">
                      <p className="font-semibold text-slate-700 mb-2">{role}</p>
                      <div className="border-b border-slate-300 mb-1 pb-1">
                        <span className="text-slate-300">___________________</span>
                      </div>
                      <p className="text-slate-400">Signature</p>
                      <div className="border-b border-slate-300 mb-1 pb-1 mt-2">
                        <span className="text-slate-300">___________________</span>
                      </div>
                      <p className="text-slate-400">Date</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Print Footer ── */}
            <div className="border-t border-slate-200 px-5 sm:px-6 py-2 text-[10px] text-slate-400 flex justify-between">
              <span>Generated by Cooperative Management System</span>
              <span>Page 1 of 1</span>
            </div>
          </div>

          {/* ─── Bottom Action Buttons (Screen only) ─── */}
          <div className="print:hidden flex flex-col sm:flex-row gap-3 mt-6">
            <Button variant="outline" className="sm:flex-1" onClick={() => setStep(2)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button
              className="sm:flex-1 bg-[#009A44] hover:bg-[#008238] text-white"
              disabled={submitting || !declarationChecked}
              onClick={handleSubmit}
            >
              {submitting ? (
                <><Loader className="animate-spin mr-2 h-4 w-4" /> Submitting...</>
              ) : (
                "Submit Application"
              )}
            </Button>
          </div>
          </>
          )}
        </div>
      )}
      {/* Step 4: Success */}
      {step === 4 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-[#009A44]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Application Submitted!</h2>
              <p className="text-sm text-slate-500 mt-1">Your loan application has been received successfully.</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 w-full space-y-2 text-left">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Amount</span>
                <span className="font-semibold">{formatCurrency(numericAmount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Duration</span>
                <span className="font-semibold">{numericTerm} months</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Monthly Installment</span>
                <span className="font-semibold">{formatCurrency(Math.round(monthlyInstallment))}</span>
              </div>
            </div>

            <p className="text-xs text-slate-400">
              Your application is now pending review. You will be notified once it's approved.
            </p>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => navigate("/member/loans")}>
                Go to Loan Dashboard
              </Button>
              <Button className="flex-1 bg-[#009A44] hover:bg-[#008238] text-white" onClick={() => {
                setStep(1);
                setAmount("");
                setPurpose("");
                setLoanTypeId("");
                setTermMonths("12");
              }}>
                Apply Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
